import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Row, Col, Form, FloatingLabel, Button, Modal } from "react-bootstrap";
import { ButtonLoader } from "Components";
import { authService } from "Service/Axios";
import {
    WORKFLOWS_CREATE_WORKFLOW_API,
    FORM_BUILDER_FORM_EDIT_URL,
    USER_URL,
} from "Constant/api";
import { useFieldList, useFormCoverImageList } from "Constant/apiHooks";
import { Validator } from "Utils/validator";
import { uploadHandler } from "Utils";
import { DEFAULT_MODULE_TYPE, IMAGE_TYPES } from "Constant/constants";
import { paths } from "Constant/paths";
import { useFolderListWorkflow } from "./useFolderListWorkflow";

export const CreateForm = ({ show, selectedForm, toggle, fetchFolderList }) => {
    const { t } = useTranslation();
    const ref = React.useRef(null);
    const navigate = useNavigate();

    const {
        formBuilder: {
            customizeForm: { root, formDesign },
        },
    } = paths;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        folderId: "",
        projectId: "",
        coverImage: "",
    });
    const [errMsg, setErrMsg] = useState({
        name: "",
        folderId: "",
        projectId: "",
        coverImage: "",
    });
    const [projectList, setProjectList] = useState([]);

    const { name, folderId, projectId, coverImage } = formData;

    const {
        coverImageList,
        setCoverImageList,
        loading: coverImageLoading,
    } = useFormCoverImageList();

    const { folderList, loading: folderLoading } = useFolderListWorkflow();

    const getMenuList = async () => {
        const data = {};
        const response = await authService(USER_URL, "POST", data);
        setProjectList(response);
    };

    useEffect(() => {
        getMenuList();
    }, []);

    useEffect(() => {
        if (selectedForm) {
            setFormData((prev) => ({
                ...prev,
                name: selectedForm.name,
                projectId: selectedForm.formCoverName,
                folderId: selectedForm.folderId,
                coverImage: selectedForm.coverImage,
            }));
        }
    }, [selectedForm]);

    useEffect(() => {
        if (selectedForm && selectedForm.coverImage) {
            if (!coverImageList.includes(selectedForm.coverImage)) {
                setCoverImageList([selectedForm.coverImage, ...coverImageList]);
            }
            // const newCoverImageList = [...coverImageList];
        }
    }, [selectedForm, coverImageList]);

    // On Change
    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        if (e.target.name === "name") {
            if (!Validator.text(e.target.value)) {
                setErrMsg((prev) => ({
                    ...prev,
                    name: t("common.messages.error.common.name.blank"),
                }));
            } else {
                setErrMsg((prev) => ({
                    ...prev,
                    name: "",
                }));
            }
        }

        if (e.target.name === "folderId") {
            if (!Validator.text(e.target.value)) {
                setErrMsg((prev) => ({
                    ...prev,
                    folderId: t("common.messages.error.common.folderId.blank"),
                }));
            } else {
                setErrMsg((prev) => ({
                    ...prev,
                    folderId: "",
                }));
            }
        }

        if (e.target.name === "projectId") {
            if (!Validator.text(e.target.value)) {
                setErrMsg((prev) => ({
                    ...prev,
                    projectId: t("common.messages.error.common.projectId.blank"),
                }));
            } else {
                setErrMsg((prev) => ({
                    ...prev,
                    projectId: "",
                }));
            }
        }

        if (e.target.name === "coverImage") {
            if (!Validator.text(e.target.value)) {
                setErrMsg((prev) => ({
                    ...prev,
                    coverImage: t("common.messages.error.common.coverImage.blank"),
                }));
            } else {
                setErrMsg((prev) => ({
                    ...prev,
                    coverImage: "",
                }));
            }
        }
    };

    // const handleUploadCoverImage = async (e) => {
    //     setLoading(true);
    //     const response = await uploadHandler(
    //         e,
    //         DEFAULT_MODULE_TYPE.coverImage,
    //         IMAGE_TYPES
    //     );
    //     if (response && response.url) {
    //         setCoverImageList([response.url, ...coverImageList]);
    //         setLoading(false);
    //     }
    // };

    const getValidation = () => {
        return new Promise((resolve) => {
            if (
                Validator.text(name) &&
                Validator.text(folderId) &&
                Validator.text(projectId)
            ) {
                resolve(true);
            } else {
                if (!Validator.text(name)) {
                    setErrMsg((prev) => ({
                        ...prev,
                        name: t("common.messages.error.common.name.blank"),
                    }));
                }
                if (!Validator.text(folderId)) {
                    setErrMsg((prev) => ({
                        ...prev,
                        folderId: t("common.messages.error.common.folderId.blank"),
                    }));
                }
                if (!Validator.text(projectId)) {
                    setErrMsg((prev) => ({
                        ...prev,
                        projectId: t("common.messages.error.common.projectId.blank"),
                    }));
                }
                resolve(false);
            }
        });
    };

    // On Submit
    const onSaveForm = async () => {
        const validationStatus = await getValidation();
        if (validationStatus) {
            setLoading(true);
            try {
                if (selectedForm?.workflowId) {
                    const payload = {
                        ...selectedForm,
                        name: name,
                        projectId: projectId,
                        folderId: folderId,
                    };
                    const response = await authService(
                        FORM_BUILDER_FORM_EDIT_URL,
                        "POST",
                        payload
                    );
                    setLoading(false);
                    if (response) {
                        if (response.success) {
                            toast.success(
                                t("common.messages.notification.success.updateForm")
                            );
                            navigate(`workflows/${response.workflowId}`);
                            fetchFolderList();
                            setCoverImageList([]);
                            toggle();
                        } else {
                            if (
                                response.message ===
                                "The form has already exist under this folder."
                            ) {
                                toast.error(
                                    t("common.messages.notification.failed.duplicateForm")
                                );
                            } else {
                                toast.error(response.message);
                            }
                        }
                    }
                } else {
                    const payload = {
                        name: name,
                        projectId: projectId,
                        folderId: folderId,
                    };
                    const response = await authService(
                        WORKFLOWS_CREATE_WORKFLOW_API,
                        "POST",
                        payload
                    );
                    setLoading(false);
                    if (response) {
                        if (response.success) {
                            toast.success(
                                t("common.messages.notification.success.createForm")
                            );
                            navigate(`workflows/${response.workflowId}`);
                            fetchFolderList();
                            setCoverImageList([]);
                            toggle();
                        } else {
                            if (
                                response.message ===
                                "The form has already exist under this folder."
                            ) {
                                toast.error(
                                    t("common.messages.notification.failed.duplicateForm")
                                );
                            } else {
                                toast.error(response.message);
                            }
                        }
                    }
                }
            } catch (error) {
                setLoading(false);
                if (error.response && error.response.data) {
                    toast.error(error.response.data.message);
                }
            }
        }
    };

    const onHide = () => {
        setCoverImageList([]);
        toggle();
    };

    return (
        <Modal
            show={show}
            onHide={toggle}
            size="lg"
            centered
            backdrop="static"
            keyboard={false}
            ref={ref}
        >
            <Modal.Header className="justify-content-center">
                <Modal.Title>Customize your Workflow</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4">
                <Row>
                    <Col lg={6} md={6} sm={12} xs={12}>
                        <FloatingLabel
                            controlId="floatingInput"
                            label='Workflow Name'
                            className="mb-3 grey-filed"
                        >
                            <Form.Control
                                type="text"
                                placeholder='Workflow Name'
                                name="name"
                                value={name}
                                onChange={handleChange}
                            />
                            {errMsg.name && <p className="error">{errMsg.name}</p>}
                        </FloatingLabel>
                    </Col>
                    <Col lg={6} md={6} sm={12} xs={12}>
                        <FloatingLabel
                            controlId="floatingSelect"
                            label={t("common.label.folder")}
                            className="mb-3 grey-filed"
                        >
                            <Form.Select
                                aria-label="Floating label select example"
                                name="folderId"
                                value={folderId}
                                onChange={handleChange}
                            >
                                <option value="">Select</option>
                                {!folderLoading &&
                                    folderList &&
                                    folderList.length > 0 &&
                                    folderList.map((folder) => (
                                        <option value={folder.folderId}>{folder.name}</option>
                                    ))}
                            </Form.Select>
                            {errMsg.folderId && <p className="error">{errMsg.folderId}</p>}
                        </FloatingLabel>
                    </Col>
                    <Col lg={6} md={6} sm={12} xs={12}>
                        <FloatingLabel
                            controlId="floatingInput"
                            label='Project Name'
                            className="mb-3 grey-filed"
                        >
                            <Form.Select
                                aria-label="Floating label select example"
                                name="projectId"
                                value={projectId}
                                onChange={handleChange}
                            >
                                <option value="">Select</option>
                                {projectList &&
                                    projectList.length > 0 &&
                                    projectList.map((folder) => (
                                        <option value={folder.gid}>{folder.name}</option>
                                    ))}
                            </Form.Select>
                            {errMsg.projectId && <p className="error">{errMsg.projectId}</p>}
                        </FloatingLabel>
                    </Col>
                </Row>

                {/* <div className="modal-title h4 mb-4">
                    {t("common.label.chooseCoverImage")}
                </div>
                <div className="cover-image-list">
                    <Row className="gy-4">
                        <Col lg={4} md={6} sm={12} xs={12}>
                            <div className="file-input-box bg-white border rounded height-equal">
                                <label
                                    htmlFor="file"
                                    className="d-block p-2 text-center w-full height-equal d-flex flex-column justify-content-center align-items-center"
                                >
                                    <span className="material-symbols-outlined align-middle mb-2">
                                        {t("common.label.upload")}
                                    </span>
                                    <h5 className="mb-0">{t("common.label.uploadYourImage")}</h5>
                                </label>
                                <input
                                    type="file"
                                    id="file"
                                    className="d-none"
                                    onChange={handleUploadCoverImage}
                                />
                            </div>
                        </Col>
                        {coverImageLoading ? (
                            <h1>Image Loading</h1>
                        ) : (
                            coverImageList &&
                            coverImageList.length > 0 &&
                            coverImageList.map((cvrImg, index) => (
                                <Col lg={4} md={6} sm={12} xs={12} key={`${index.toString()}`}>
                                    <div className="cover-form-box p-2 rounded border relative">
                                        <Form.Check
                                            className="form-check-success"
                                            type="radio"
                                            id={`default-${index.toString()}`}
                                            name="coverImage"
                                            label={projectId}
                                            value={cvrImg}
                                            checked={cvrImg === coverImage}
                                            onChange={handleChange}
                                        />
                                        <img src={cvrImg} alt={`coverImage${index + 1}`} />
                                    </div>
                                </Col>
                            ))
                        )}
                    </Row>
                    {errMsg.coverImage && <p className="error">{errMsg.coverImage}</p>}
                </div> */}

            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-center pb-4">
                <Button variant="outline-primary" onClick={onHide}>
                    {t("common.button.cancel")}
                </Button>
                {!loading ? (
                    <Button variant="primary" onClick={onSaveForm}>
                        {selectedForm?.workflowId
                            ? t("common.button.update")
                            : t("common.button.create")}
                    </Button>
                ) : (
                    <ButtonLoader />
                )}
            </Modal.Footer>
        </Modal>
    );
};
